import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { autorun, extendObservable, makeAutoObservable, observable, ObservableMap } from 'mobx';
import { useState, useEffect } from 'react';
import ModalDelegate from 'store/general/modalDelegate';
import { CarrierUiPresenter } from 'store/pages/rfq/initiate-rfq/pick-carriers/pageStore';

interface ContextualProps {
  carrier: CarrierUiPresenter;
  updateRecipients: (carrier: CarrierUiPresenter) => void;
}

interface ContactsFields extends DefaultFormFields {
  primaryEmail: FormControl<string>;
  [key: string]: FormControl<string>;
}

export class ManageContactsModalStore {
  public manageContactsModal = new ModalDelegate();
  private _carrier: CarrierUiPresenter;
  private _updateRecipients: (carrier: CarrierUiPresenter) => void;
  private _lastCC: number = 0;
  private _ccFields: ObservableMap<string, boolean> = observable.map({});

  private _form = new FormController<ContactsFields>({
    primaryEmail: new FormControl<string>('', { validations: ['isEmailValid'] }),
  });

  public get form(): FormController<ContactsFields> {
    return this._form;
  }

  public get showManageContactsModal(): boolean {
    return this.manageContactsModal.isVisible;
  }

  public checkEmailsUniqueness(): boolean {
    const { primaryEmail, ...ccFields } = this.form.getFormDataObject();
    // Remove empty CC fields that were created but left unfilled.
    // This prevents false uniqueness warning for empty form fields.
    const ccEmails = Object.values(ccFields).filter((email) => email.trim() !== '');
    const emails = [primaryEmail, ...ccEmails];
    return new Set(emails).size === emails.length;
  }

  public get ccFields(): string[] {
    return Array.from(this._ccFields.keys());
  }

  public openManageContactsModal(): void {
    // The form should be created on modal open to ensure all previously added custom CC fields
    // are removed, as they don't reset with the general form reset approach.
    this.initializeFormWithData();
    this.manageContactsModal.open();
  }

  private initializeFormWithData(): void {
    this._form = new FormController<ContactsFields>({
      primaryEmail: new FormControl<string>(this._carrier.defaultRecipientEmail, {
        validations: ['isEmailValid'],
      }),
    });

    if (this._carrier.ccRecipients.length > 0) {
      this._carrier.ccRecipients.forEach((cc) => {
        this._lastCC += 1;

        extendObservable(this._form.fields, {
          [`ccEmail${this._lastCC}`]: new FormControl<string>(cc, { validations: ['isEmailValid'] }),
        });
        this._ccFields.set(`ccEmail${this._lastCC}`, true);
      });
    }
  }

  public closeManageContactsModal(): void {
    this.clearModalData();
    this.manageContactsModal.close();
  }

  public addCc(): void {
    this._lastCC += 1;
    extendObservable(this._form.fields, {
      [`ccEmail${this._lastCC}`]: new FormControl<string>('', { validations: ['isEmailValid'] }),
    });
    this._ccFields.set(`ccEmail${this._lastCC}`, true);
  }

  public removeCc(ccId: string): void {
    delete this._form.fields[ccId];
    this._ccFields.delete(ccId);
  }

  private clearModalData(): void {
    this._ccFields.clear();
    this._form.resetForm();
  }

  public getCcField(fieldKey: string): FormControl<string> {
    if (!this._form.fields[fieldKey]) {
      throw new Error(`Attempted to access uninitialized field of key: ${fieldKey}`);
    }
    return this._form.fields[fieldKey];
  }

  public saveContacts(): void {
    const areEmailsUnique = this.checkEmailsUniqueness();

    if (this.form.basicFieldsValidationsPassed && areEmailsUnique) {
      const { primaryEmail, ...ccFields } = this.form.getFormDataObject();
      this._carrier.defaultRecipientEmail = primaryEmail;
      this._carrier.ccRecipients = Object.values(ccFields);
      this._updateRecipients(this._carrier);

      this.closeManageContactsModal();
    }
  }

  constructor({ carrier, updateRecipients }: ContextualProps) {
    this._carrier = carrier;
    this._updateRecipients = updateRecipients;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

export function useManageContactsModalStore({
  carrier,
  updateRecipients,
}: ContextualProps): ManageContactsModalStore | null {
  const [store, setStore] = useState<ManageContactsModalStore | null>(
    () => new ManageContactsModalStore({ carrier, updateRecipients }),
  );

  useEffect(() => {
    autorun(() => {
      setStore(new ManageContactsModalStore({ carrier, updateRecipients }));
    });

    return (): void => {
      setStore(null);
    };
  }, [carrier, updateRecipients]);
  return store;
}
